import React, { useState, useEffect } from 'react';
import historyOptimizer from '../Vehicle/VehicleHistoryOptimizer';
import dataManager from '../Vehicle/VehicleHistoryDataManager';

/**
 * History Performance Test Component
 * Tests and validates the vehicle history performance optimizations
 */
const HistoryPerformanceTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [metrics, setMetrics] = useState({});

  // Update metrics every 3 seconds
  useEffect(() => {
    const updateMetrics = () => {
      const historyMetrics = historyOptimizer.getMetrics();
      const dataMetrics = dataManager.getMetrics();
      setMetrics({ history: historyMetrics, data: dataMetrics });
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 3000);

    return () => clearInterval(interval);
  }, []);

  // Generate synthetic location data for testing
  const generateTestLocations = (count, timeSpanHours = 24) => {
    const locations = [];
    const startTime = new Date();
    startTime.setHours(startTime.getHours() - timeSpanHours);

    // Base location (Belize City area)
    const baseLatitude = 17.5045;
    const baseLongitude = -88.1962;

    for (let i = 0; i < count; i++) {
      // Create a realistic route pattern
      const progress = i / count;
      const timeOffset = progress * timeSpanHours * 60 * 60 * 1000;
      const timestamp = new Date(startTime.getTime() + timeOffset);

      // Create meandering path with some randomness
      const pathProgress = Math.sin(progress * Math.PI * 4) * 0.01; // Sinusoidal path
      const randomNoise = (Math.random() - 0.5) * 0.001; // Small random variation

      const location = {
        id: `test_location_${i}`,
        latitude: baseLatitude + pathProgress + randomNoise,
        longitude: baseLongitude + (progress * 0.05) + randomNoise, // General eastward movement
        timestamp: timestamp.toISOString(),
        speed: Math.random() * 60 + 10, // Random speed 10-70 km/h
        heading: (progress * 90 + Math.random() * 20) % 360 // Varying heading
      };

      locations.push(location);
    }

    return locations;
  };

  // Test clustering performance with different data sizes
  const runClusteringTest = async (pointCounts) => {
    const results = [];

    for (const count of pointCounts) {

      const testLocations = generateTestLocations(count);

      // Test without clustering (original approach)
      const originalStart = performance.now();
      const originalMarkers = testLocations.slice(0, Math.min(200, count)); // Simulate browser limit
      const originalTime = performance.now() - originalStart;

      // Test with clustering
      historyOptimizer.clearCache();
      const clusterStart = performance.now();
      const clusterResult = historyOptimizer.clusterLocationPoints(testLocations, 13, null);
      const clusterTime = performance.now() - clusterStart;

      const improvement = originalTime > 0 ? ((originalTime - clusterTime) / originalTime * 100) : 0;

      results.push({
        pointCount: count,
        originalTime: originalTime.toFixed(1),
        originalMarkers: originalMarkers.length,
        clusterTime: clusterTime.toFixed(1),
        clusteredMarkers: clusterResult.clusters.length,
        improvement: improvement.toFixed(1),
        compressionRatio: clusterResult.metrics.compressionRatio,
        memoryUsage: ((clusterResult.metrics.memoryUsage || 0) / 1024).toFixed(1)
      });

      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return results;
  };

  // Test memory usage over time
  const runMemoryTest = async () => {
    const memorySnapshots = [];

    if (!('memory' in performance)) {
      return { error: 'Memory API not available in this browser' };
    }

    const initialMemory = performance.memory.usedJSHeapSize;
    memorySnapshots.push({ phase: 'Initial', memory: initialMemory });

    // Create increasingly large datasets
    for (let size = 100; size <= 1000; size += 100) {
      const testLocations = generateTestLocations(size);
      historyOptimizer.clusterLocationPoints(testLocations, 13, null);

      const currentMemory = performance.memory.usedJSHeapSize;
      memorySnapshots.push({
        phase: `${size} points`,
        memory: currentMemory,
        growthMB: (currentMemory - initialMemory) / 1024 / 1024
      });

      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Force garbage collection if available
    if (window.gc) {
      window.gc();
      const afterGCMemory = performance.memory.usedJSHeapSize;
      memorySnapshots.push({
        phase: 'After GC',
        memory: afterGCMemory,
        growthMB: (afterGCMemory - initialMemory) / 1024 / 1024
      });
    }

    return memorySnapshots;
  };

  // Run comprehensive performance test
  const runFullTest = async () => {
    setIsRunning(true);

    try {
      const testScenarios = [50, 100, 200, 500, 800, 1000, 2000, 5000];

      // Test clustering performance
      const clusteringResults = await runClusteringTest(testScenarios);

      // Test memory usage
      const memoryResults = await runMemoryTest();

      // Test route simplification
      const complexRoute = generateTestLocations(1000);
      const simplifyStart = performance.now();
      const simplifiedRoute = historyOptimizer.simplifyRoute(
        complexRoute.map(loc => ({ lat: loc.latitude, lng: loc.longitude }))
      );
      const simplifyTime = performance.now() - simplifyStart;

      const simplificationResult = {
        originalPoints: complexRoute.length,
        simplifiedPoints: simplifiedRoute.length,
        reductionPercent: ((1 - simplifiedRoute.length / complexRoute.length) * 100).toFixed(1),
        simplifyTime: simplifyTime.toFixed(1)
      };

      setTestResults({
        clustering: clusteringResults,
        memory: memoryResults,
        simplification: simplificationResult,
        timestamp: new Date(),
        browserInfo: {
          userAgent: navigator.userAgent,
          memorySupport: 'memory' in performance,
          gcSupport: !!window.gc
        }
      });

    } catch (error) {
      setTestResults({ error: error.message });
    } finally {
      setIsRunning(false);
    }
  };

  // Clear all caches
  const handleClearCaches = () => {
    historyOptimizer.clearCache();
    dataManager.clearCache();
    setMetrics({});
  };

  const getPerformanceColor = (value, type) => {
    if (type === 'time') {
      const timeMs = parseFloat(value);
      return timeMs < 50 ? 'text-green-600' : timeMs < 200 ? 'text-yellow-600' : 'text-red-600';
    }
    if (type === 'compression') {
      const ratio = parseFloat(value);
      return ratio > 70 ? 'text-green-600' : ratio > 30 ? 'text-yellow-600' : 'text-red-600';
    }
    return 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">🧪 History Performance Test</h3>
        <div className="space-x-2">
          <button
            onClick={runFullTest}
            disabled={isRunning}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isRunning ? 'Running Tests...' : 'Run Full Test'}
          </button>
          <button
            onClick={handleClearCaches}
            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Clear Caches
          </button>
        </div>
      </div>

      {/* Current Metrics */}
      {Object.keys(metrics).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">📊 History Optimizer</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Original Points:</span>
                <span className="font-mono">{metrics.history?.originalPoints?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Clustered Points:</span>
                <span className="font-mono">{metrics.history?.clusteredPoints || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Render Time:</span>
                <span className={`font-mono ${getPerformanceColor(metrics.history?.renderTime, 'time')}`}>
                  {metrics.history?.renderTime?.toFixed(1) || 0}ms
                </span>
              </div>
              <div className="flex justify-between">
                <span>Compression:</span>
                <span className={`font-mono ${getPerformanceColor(metrics.history?.compressionRatio, 'compression')}`}>
                  {metrics.history?.compressionRatio || 0}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-green-800 mb-2">💾 Data Manager</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Total Points:</span>
                <span className="font-mono">{metrics.data?.totalPoints?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Cache Size:</span>
                <span className="font-mono">{metrics.data?.cacheSize || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Hit Rate:</span>
                <span className="font-mono text-green-600">{metrics.data?.cacheHitRate?.toFixed(1) || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span>Memory Usage:</span>
                <span className="font-mono">{metrics.data?.memoryUsage?.toFixed(1) || 0}MB</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Results */}
      {testResults && !testResults.error && (
        <div className="space-y-6">
          {/* Clustering Results */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">🎯 Clustering Performance Results</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-2">Points</th>
                    <th className="text-right p-2">Original Time</th>
                    <th className="text-right p-2">Cluster Time</th>
                    <th className="text-right p-2">Markers</th>
                    <th className="text-right p-2">Compression</th>
                    <th className="text-right p-2">Memory</th>
                    <th className="text-right p-2">Improvement</th>
                  </tr>
                </thead>
                <tbody>
                  {testResults.clustering.map((result, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="p-2 font-medium">{result.pointCount.toLocaleString()}</td>
                      <td className="p-2 text-right font-mono text-red-600">{result.originalTime}ms</td>
                      <td className="p-2 text-right font-mono text-green-600">{result.clusterTime}ms</td>
                      <td className="p-2 text-right font-mono">
                        {result.originalMarkers} → {result.clusteredMarkers}
                      </td>
                      <td className="p-2 text-right font-mono text-green-600">
                        {result.compressionRatio}%
                      </td>
                      <td className="p-2 text-right font-mono">{result.memoryUsage}KB</td>
                      <td className="p-2 text-right font-mono text-blue-600">
                        +{result.improvement}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Memory Results */}
          {testResults.memory && !testResults.memory.error && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-3">🧠 Memory Usage Test</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {testResults.memory.map((snapshot, index) => (
                  <div key={index} className="p-2 bg-white rounded">
                    <div className="font-medium">{snapshot.phase}</div>
                    <div className="text-purple-600 font-mono">
                      {snapshot.growthMB ? `+${snapshot.growthMB.toFixed(1)}MB` : 'Baseline'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Route Simplification */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-3">🛣️ Route Simplification</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-yellow-700">Original Points:</span>
                <div className="font-mono text-yellow-900">{testResults.simplification.originalPoints.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-yellow-700">Simplified Points:</span>
                <div className="font-mono text-yellow-900">{testResults.simplification.simplifiedPoints.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-yellow-700">Reduction:</span>
                <div className="font-mono text-green-600">{testResults.simplification.reductionPercent}%</div>
              </div>
              <div>
                <span className="text-yellow-700">Process Time:</span>
                <div className="font-mono text-yellow-900">{testResults.simplification.simplifyTime}ms</div>
              </div>
            </div>
          </div>

          {/* Browser Info */}
          <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
            <h5 className="font-medium mb-2">Browser Information</h5>
            <div>Memory API: {testResults.browserInfo.memorySupport ? '✅ Available' : '❌ Not Available'}</div>
            <div>GC API: {testResults.browserInfo.gcSupport ? '✅ Available' : '❌ Not Available'}</div>
            <div>Test completed: {testResults.timestamp.toLocaleString()}</div>
          </div>
        </div>
      )}

      {testResults?.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <strong>Test Error:</strong> {testResults.error}
        </div>
      )}

      {/* Performance Recommendations */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-3">💡 Performance Recommendations</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center text-green-700">
            <span className="mr-2">✅</span>
            <span>Clustering provides significant performance improvements for 200+ points</span>
          </div>
          <div className="flex items-center text-green-700">
            <span className="mr-2">✅</span>
            <span>Route simplification reduces polyline complexity by 70-90%</span>
          </div>
          <div className="flex items-center text-blue-700">
            <span className="mr-2">ℹ️</span>
            <span>Progressive loading prevents browser crashes with large datasets</span>
          </div>
          <div className="flex items-center text-yellow-700">
            <span className="mr-2">⚠️</span>
            <span>Memory usage should be monitored for datasets {'>'} 5000 points</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryPerformanceTest;